/**
 * Process Owner Overview Tab (Tab 4)
 * 
 * High-level summary showing certification status matrix across the fiscal year
 * Features:
 * - Table with process names and Q1-Q4 columns
 * - Sign-off dates or "-" for each quarter
 * - Navigation to Process Hierarchy on process click
 */

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Dropdown, message, Modal, Radio, Button, Tooltip, Table } from 'antd';
import type { MenuProps, RadioChangeEvent, TableColumnsType } from 'antd';
import { MoreOutlined, DownloadOutlined } from '@ant-design/icons';
import ExportButton from '../../../../../components/common/ExportButton';
import styled from 'styled-components';
import ExportPanel from '../../../../../components/common/ExportPanel';
import { RootState, AppDispatch } from '../../../../../store';
import { setProcessCertifications } from '../../../../../store/slices/certificationSlice';
import {
  getProcessCertificationsForOwner,
  formatCertificationDate,
  downloadCertification,
  type ProcessCertification,
  type Quarter,
} from '../../../../../services/certificationService';

// ========================================
// Styled Components
// ========================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TableContainer = styled.div`
  background: #E0E7FF;
  border-radius: 6px;
  border: 1px solid #E0E7FF;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  padding: 0 0 0 2px;

  .ant-table {
    background: #FFFFFF;
  }

  .ant-table-thead > tr > th {
    background: #F9FAFB;
    padding: 14px 16px;
    font-family: Inter;
    font-weight: 500;
    font-size: 12px;
    color: #6B7280;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    
    &:last-child {
      border-right: none;
    }
    
    &::before {
      display: none;
    }
  }

  .ant-table-tbody > tr > td {
    padding: 14px 16px;
    font-family: Inter;
    font-size: 14px;
    color: #1F2937;
    border-bottom: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    
    &:last-child {
      border-right: none;
    }
  }

  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }
`;

const ProcessText = styled.span`
  font-family: Inter;
  font-size: 14px;
  color: #1F2937;
`;

const DateText = styled.span`
  font-family: Inter;
  font-size: 14px;
  color: #6B7280;
`;

const CompletedDate = styled.span`
  color: #059669;
`;

const EmptyStateWrapper = styled.div`
  text-align: center;
  padding: 122px 20px;
  background: white;
  border-radius: 6px;
`;

const EmptyStateText = styled.div`
  color: #1F2937;
  font-family: Inter;
  font-size: 15px;
  font-weight: 500;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  min-height: 400px;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ActionButton = styled.button`
  background: #F8FAFC;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  transition: all 0.3s;

  &:hover {
    color: #1F2937;
    background: #EFF6FF;
  }

  .anticon {
    font-size: 20px;
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    flex-shrink: 0;
  }
`;

const MenuItemText = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.005em;
  color: inherit;
`;

// Download Modal Styled Components
const DownloadModalContent = styled.div`
  text-align: center;
  padding: 8px 0;
`;

const DownloadModalTitle = styled.h3`
  font-family: Inter;
  font-weight: 700;
  font-size: 20px;
  color: #1F2937;
  margin: 0 0 8px 0;
`;

const DownloadModalSubtitle = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #6B7280;
  margin-bottom: 24px;
`;

const QuarterSelectionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
`;

const QuarterOption = styled.div<{ $isAvailable: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid ${props => props.$isAvailable ? '#E5E7EB' : '#F3F4F6'};
  border-radius: 8px;
  background: ${props => props.$isAvailable ? '#FFFFFF' : '#F9FAFB'};
  cursor: ${props => props.$isAvailable ? 'pointer' : 'not-allowed'};
  transition: all 0.2s;
  
  &:hover {
    ${props => props.$isAvailable && `
      border-color: #2563EB;
      background: #EFF6FF;
    `}
  }

  .ant-radio-wrapper {
    width: 100%;
    margin: 0;
    
    span:last-child {
      flex: 1;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }
`;

const QuarterLabel = styled.span<{ $isAvailable: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  margin-right: 4px;
  color: ${props => props.$isAvailable ? '#1F2937' : '#9CA3AF'};
`;

const QuarterDate = styled.span<{ $isAvailable: boolean }>`
  font-family: Inter;
  font-size: 12px;
  color: ${props => props.$isAvailable ? '#6B7280' : '#D1D5DB'};
`;

const DownloadButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
`;

const DownloadCancelButton = styled(Button)`
  min-width: 120px;
  height: 46px;
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  border-radius: 8px;
  border: 1px solid #2563EB;
  color: #2563EB;
  
  &:hover {
    border-color: #1D4ED8 !important;
    color: #1D4ED8 !important;
  }
`;

const DownloadConfirmButton = styled(Button)`
  min-width: 120px;
  height: 46px;
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  border-radius: 8px;
  background: #2563EB;
  border-color: #2563EB;
  color: #FFFFFF;
  
  &:hover:not(:disabled) {
    background: #1D4ED8 !important;
    border-color: #1D4ED8 !important;
    color: #FFFFFF !important;
  }
  
  &:disabled {
    background: #9CA3AF !important;
    border-color: #9CA3AF !important;
  }
`;

const StyledDownloadModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    padding: 24px;
  }
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
`;

// ========================================
// Component
// ========================================

// Download modal state interface
interface DownloadModalState {
  isOpen: boolean;
  process: ProcessCertification | null;
  selectedQuarter: Quarter | null;
}

const POOverviewTab: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const { processCertifications, selectedProcessOwnerId } = useSelector((state: RootState) => state.certification);
  
  const [loading, setLoading] = useState(true);
  
  // Download certification modal state
  const [downloadModal, setDownloadModal] = useState<DownloadModalState>({
    isOpen: false,
    process: null,
    selectedQuarter: null,
  });
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Export panel state
  const [isExportPanelOpen, setIsExportPanelOpen] = useState(false);
  
  // Ref to prevent duplicate API calls
  const lastFetchedRef = useRef<string | null>(null);
  
  // Fetch certification list - reuses same API as Admin Dashboard
  // Uses selectedProcessOwnerId from Redux (consistent with other tabs)
  useEffect(() => {
    const fetchData = async () => {
      if (!selectedReport || !selectedProcessOwnerId) {
        setLoading(false);
        return;
      }
      
      // Check cache key to prevent duplicate calls
      const cacheKey = `${selectedReport.annualReportId}-${selectedProcessOwnerId}`;
      if (lastFetchedRef.current === cacheKey) {
        // console.log('⏭️ POOverviewTab: Skipping duplicate fetch for:', cacheKey);
        setLoading(false);
        return;
      }
      
      lastFetchedRef.current = cacheKey;
      setLoading(true);
      
      try {
        // console.log('📋 POOverviewTab: Fetching certifications...', {
        //   annualReportId: selectedReport.annualReportId,
        //   processOwnerId: selectedProcessOwnerId,
        // });
        
        // Use same API as AdminDashboardTab - getProcessCertificationsForOwner
        const certifications = await getProcessCertificationsForOwner(
          selectedReport.annualReportId,
          selectedProcessOwnerId
        );
        dispatch(setProcessCertifications(certifications));
        // console.log('✅ POOverviewTab: Certifications loaded:', certifications.length);
      } catch (error) {
        console.error('Error fetching certification list:', error);
        // Reset cache on error to allow retry
        lastFetchedRef.current = null;
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [selectedReport, selectedProcessOwnerId, dispatch]);
  
  // Open download certification modal
  const handleOpenDownloadModal = useCallback((process: ProcessCertification) => {
    // Find the first available quarter (one that has certification date)
    let defaultQuarter: Quarter | null = null;
    if (process.q1SignOffDate) defaultQuarter = 1;
    else if (process.q2SignOffDate) defaultQuarter = 2;
    else if (process.q3SignOffDate) defaultQuarter = 3;
    else if (process.q4SignOffDate) defaultQuarter = 4;
    
    setDownloadModal({
      isOpen: true,
      process,
      selectedQuarter: defaultQuarter,
    });
  }, []);
  
  // Close download modal
  const handleCloseDownloadModal = useCallback(() => {
    setDownloadModal({
      isOpen: false,
      process: null,
      selectedQuarter: null,
    });
  }, []);
  
  // Handle quarter selection change
  const handleQuarterChange = useCallback((e: RadioChangeEvent) => {
    setDownloadModal(prev => ({
      ...prev,
      selectedQuarter: e.target.value as Quarter,
    }));
  }, []);
  
  // Handle download confirmation
  const handleConfirmDownload = useCallback(async () => {
    if (!downloadModal.process || !downloadModal.selectedQuarter || !selectedProcessOwnerId) {
      return;
    }
    
    setIsDownloading(true);
    try {
      await downloadCertification(
        selectedProcessOwnerId,
        downloadModal.process.processId,
        downloadModal.selectedQuarter
      );
      message.success(`Downloaded Q${downloadModal.selectedQuarter} certification for ${downloadModal.process.processLabel}`);
      handleCloseDownloadModal();
    } catch (error: any) {
      console.error('Download failed:', error);
      message.error(error?.message || 'Failed to download certification');
    } finally {
      setIsDownloading(false);
    }
  }, [downloadModal, selectedProcessOwnerId, handleCloseDownloadModal]);
  
  // Get available quarters with their dates for the modal
  const getQuarterOptions = useCallback(() => {
    if (!downloadModal.process) return [];
    
    const { q1SignOffDate, q2SignOffDate, q3SignOffDate, q4SignOffDate } = downloadModal.process;
    
    return [
      { quarter: 1 as Quarter, date: q1SignOffDate, label: 'Q1' },
      { quarter: 2 as Quarter, date: q2SignOffDate, label: 'Q2' },
      { quarter: 3 as Quarter, date: q3SignOffDate, label: 'Q3' },
      { quarter: 4 as Quarter, date: q4SignOffDate, label: 'Q4' },
    ];
  }, [downloadModal.process]);

  // Export handlers
  const handleOpenExportPanel = useCallback(() => {
    setIsExportPanelOpen(true);
  }, []);

  const handleCloseExportPanel = useCallback(() => {
    setIsExportPanelOpen(false);
  }, []);

  // Prepare export data with formatted dates
  const exportData = useMemo(() => {
    return processCertifications.map((process, index) => ({
      index: index + 1,
      processLabel: process.processLabel,
      q1SignOffDate: formatCertificationDate(process.q1SignOffDate),
      q2SignOffDate: formatCertificationDate(process.q2SignOffDate),
      q3SignOffDate: formatCertificationDate(process.q3SignOffDate),
      q4SignOffDate: formatCertificationDate(process.q4SignOffDate),
    }));
  }, [processCertifications]);
  
  // Get menu items for process action
  const getProcessMenuItems = useCallback((process: ProcessCertification): MenuProps['items'] => {
    // Check if any certification date exists for this process
    const hasAnyCertification = !!(
      process.q1SignOffDate ||
      process.q2SignOffDate ||
      process.q3SignOffDate ||
      process.q4SignOffDate
    );

    const downloadMenuItem = (
      <MenuItemContent>
        <DownloadOutlined />
        <MenuItemText>Download Certification</MenuItemText>
      </MenuItemContent>
    );

    return [
      {
        key: 'download',
        label: hasAnyCertification ? downloadMenuItem : (
          <Tooltip title="No certifications completed for this process" placement="top">
            <div>{downloadMenuItem}</div>
          </Tooltip>
        ),
        disabled: !hasAnyCertification,
        onClick: () => handleOpenDownloadModal(process),
      },
    ];
  }, [handleOpenDownloadModal]);
  
  // Render date cell
  const renderDateCell = useCallback((date: string | null) => {
    if (!date) return <DateText>-</DateText>;
    return <CompletedDate>{formatCertificationDate(date)}</CompletedDate>;
  }, []);

  // Table columns definition
  const columns: TableColumnsType<ProcessCertification> = useMemo(() => [
    {
      title: '#',
      key: 'index',
      width: 50,
      align: 'center' as const,
      render: (_: unknown, __: ProcessCertification, index: number) => index + 1,
    },
    {
      title: 'Process',
      dataIndex: 'processLabel',
      key: 'processLabel',
      render: (text: string) => <ProcessText>{text}</ProcessText>,
    },
    {
      title: 'Q1',
      dataIndex: 'q1SignOffDate',
      key: 'q1SignOffDate',
      width: 120,
      align: 'center' as const,
      render: (date: string | null) => renderDateCell(date),
    },
    {
      title: 'Q2',
      dataIndex: 'q2SignOffDate',
      key: 'q2SignOffDate',
      width: 120,
      align: 'center' as const,
      render: (date: string | null) => renderDateCell(date),
    },
    {
      title: 'Q3',
      dataIndex: 'q3SignOffDate',
      key: 'q3SignOffDate',
      width: 120,
      align: 'center' as const,
      render: (date: string | null) => renderDateCell(date),
    },
    {
      title: 'Q4',
      dataIndex: 'q4SignOffDate',
      key: 'q4SignOffDate',
      width: 120,
      align: 'center' as const,
      render: (date: string | null) => renderDateCell(date),
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      align: 'center' as const,
      render: (_: unknown, record: ProcessCertification) => (
        <Dropdown
          menu={{ items: getProcessMenuItems(record) }}
          trigger={['click']}
          placement="bottomRight"
        >
          <ActionButton onClick={(e) => e.stopPropagation()}>
            <MoreOutlined />
          </ActionButton>
        </Dropdown>
      ),
    },
  ], [renderDateCell, getProcessMenuItems]);
  
  if (loading) {
    return (
      <LoadingContainer>
        <div className="spinner" />
      </LoadingContainer>
    );
  }
  
  if (processCertifications.length === 0) {
    return (
      <EmptyStateWrapper>
        <EmptyStateText>No processes assigned for this fiscal year.</EmptyStateText>
      </EmptyStateWrapper>
    );
  }
  
  return (
    <Container>
      <TableHeader>
        <ExportButton onClick={handleOpenExportPanel} />
      </TableHeader>
      
      <TableContainer>
        <Table
          dataSource={processCertifications}
          columns={columns}
          rowKey="processId"
          pagination={false}
          size="middle"
        />
      </TableContainer>
      
      {/* Download Certification Modal */}
      <StyledDownloadModal
        open={downloadModal.isOpen}
        onCancel={handleCloseDownloadModal}
        footer={null}
        centered
        closable={true}
        width={450}
      >
        <DownloadModalContent>
          <DownloadModalTitle>Download Certification</DownloadModalTitle>
          <DownloadModalSubtitle>
            {downloadModal.process?.processLabel}
          </DownloadModalSubtitle>
          
          <QuarterSelectionGroup>
            <Radio.Group
              value={downloadModal.selectedQuarter}
              onChange={handleQuarterChange}
              style={{ width: '100%' }}
            >
              {getQuarterOptions().map(({ quarter, date, label }) => {
                const isAvailable = !!date;
                return (
                  <QuarterOption key={quarter} $isAvailable={isAvailable}>
                    <Radio style={{gap: 4, alignItems: 'center'}} value={quarter} disabled={!isAvailable}>
                      <QuarterLabel $isAvailable={isAvailable}>{label}</QuarterLabel>
                      <QuarterDate $isAvailable={isAvailable}>
                        {isAvailable ? formatCertificationDate(date) : 'Not certified'}
                      </QuarterDate>
                    </Radio>
                  </QuarterOption>
                );
              })}
            </Radio.Group>
          </QuarterSelectionGroup>
          
          <DownloadButtonGroup>
            <DownloadCancelButton onClick={handleCloseDownloadModal} disabled={isDownloading}>
              Cancel
            </DownloadCancelButton>
            <DownloadConfirmButton
              type="primary"
              icon={<DownloadOutlined />}
              loading={isDownloading}
              disabled={!downloadModal.selectedQuarter}
              onClick={handleConfirmDownload}
            >
              Download
            </DownloadConfirmButton>
          </DownloadButtonGroup>
        </DownloadModalContent>
      </StyledDownloadModal>

      {/* Export Panel */}
      <ExportPanel
        open={isExportPanelOpen}
        onClose={handleCloseExportPanel}
        data={exportData}
        columns={[
          { key: 'index', title: '#' },
          { key: 'processLabel', title: 'Process' },
          { key: 'q1SignOffDate', title: 'Q1' },
          { key: 'q2SignOffDate', title: 'Q2' },
          { key: 'q3SignOffDate', title: 'Q3' },
          { key: 'q4SignOffDate', title: 'Q4' },
        ]}
        fileName="Certification_Overview"
        title="Process Certification Overview"
      />
    </Container>
  );
};

export default POOverviewTab;

